import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToHash } from "@/components/Nav";

const LINES = [
  { text: "Enraizados", gold: false },
  { text: "e edificados", gold: true },
  { text: "nele.", gold: false },
];

const STATS = [
  ["SEG & QUI", "2x por semana"],
  ["19:00", "Turma Kids"],
  ["20:30", "Turma Adultos"],
  ["100%", "Gratuito"],
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sunY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-16" data-testid="hero-section">
      <motion.div
        style={{ y: sunY }}
        aria-hidden="true"
        className="absolute right-[-15%] top-[8%] w-[60vw] h-[60vw] max-w-[820px] max-h-[820px] rounded-full opacity-60"
      >
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, rgba(220,38,38,0.55) 0%, rgba(127,29,29,0.35) 42%, transparent 70%)" }} />
      </motion.div>
      <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:72px_72px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-[1.2fr_0.8fr] gap-14 items-center w-full">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-xs tracking-[0.3em] text-gold mb-8"
            data-testid="hero-eyebrow"
          >
            PROJETO SOCIAL DE JIU-JITSU — SOROCABA / SP
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.04]" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.gold ? "text-gold" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.35 + i * 0.18, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-5 font-mono text-xs tracking-[0.3em] text-gold/80"
            data-testid="hero-verse"
          >
            — COLOSSENSES 2:7
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-5 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
            data-testid="hero-subtext"
          >
            O Projeto Bonsai BJJ, liderado pelo Prof. Marciano, transforma a vida de crianças e
            adultos da comunidade Além Ponte através do Jiu-Jitsu — gratuito e aberto a todo o
            público, amparando também crianças de casas de abrigo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-doar"
              onClick={() => scrollToHash("#doacoes")}
              className="font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber hover:shadow-[0_0_32px_rgba(212,175,55,0.45)] hover:-translate-y-0.5 transition-all duration-300"
            >
              Faça sua Doação
            </button>
            <button
              data-testid="hero-cta-horarios"
              onClick={() => scrollToHash("#horarios")}
              className="font-display font-semibold border border-gold/40 text-gold px-8 py-3.5 rounded-full hover:bg-gold/10 hover:border-gold transition-all duration-300"
            >
              Conheça os Horários
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-white/10 pt-8"
            data-testid="hero-stats"
          >
            {STATS.map(([value, label]) => (
              <div key={label}>
                <p className="font-mono text-lg text-gold">{value}</p>
                <p className="text-xs text-zinc-500 mt-1">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: logoY }} className="hidden lg:flex justify-center relative" data-testid="hero-emblem">
          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative"
          >
            <div aria-hidden="true" className="absolute -inset-10 rounded-full" style={{ background: "radial-gradient(circle, rgba(220,38,38,0.4) 0%, transparent 65%)" }} />
            <motion.img
              src="/assets/logo.jpeg"
              alt="Emblema Projeto Bonsai BJJ - Prof. Marciano"
              initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-80 h-80 xl:w-[26rem] xl:h-[26rem] rounded-full object-cover ring-2 ring-gold/60 shadow-[0_0_90px_rgba(220,38,38,0.35)] hover:rotate-3 transition-transform duration-700"
            />
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToHash("#historia")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-zinc-500 hover:text-gold transition-colors"
        aria-label="Rolar para baixo"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ChevronDown size={28} />
        </motion.div>
      </motion.button>
    </section>
  );
}
